import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  ResponsiveContainer,
} from 'recharts';
import { Refresh, Download } from '@mui/icons-material';

// Sample analytics data
const collectionEfficiencyData = [
  { month: 'Jan', collected: 85000, target: 100000, efficiency: 85 },
  { month: 'Feb', collected: 92000, target: 105000, efficiency: 87.6 },
  { month: 'Mar', collected: 98000, target: 110000, efficiency: 89.1 },
  { month: 'Apr', collected: 87000, target: 100000, efficiency: 87 },
  { month: 'May', collected: 105000, target: 115000, efficiency: 91.3 },
  { month: 'Jun', collected: 112000, target: 120000, efficiency: 93.3 },
];

const agentEfficiencyData = [
  { agent: 'Data Agent', tasks: 1250, success: 1225, efficiency: 98 },
  { agent: 'Scoring Agent', tasks: 1180, success: 1133, efficiency: 96 },
  { agent: 'Allocation Agent', tasks: 1200, success: 1128, efficiency: 94 },
  { agent: 'Outreach Agent', tasks: 980, success: 901, efficiency: 92 },
  { agent: 'Orchestrator', tasks: 1300, success: 1287, efficiency: 99 },
];

const riskDistributionData = [
  { name: 'Low Risk (0-40)', value: 35, color: '#4caf50' },
  { name: 'Medium Risk (41-70)', value: 40, color: '#ff9800' },
  { name: 'High Risk (71-100)', value: 25, color: '#f44336' },
];

const communicationChannelData = [
  { channel: 'Email', sent: 450, successful: 380, rate: 84.4 },
  { channel: 'SMS', sent: 320, successful: 285, rate: 89.1 },
  { channel: 'Phone', sent: 180, successful: 145, rate: 80.6 },
  { channel: 'Letter', sent: 95, successful: 78, rate: 82.1 },
];

const recoveryTrendData = [
  { week: 'Week 1', amount: 45000, cases: 25 },
  { week: 'Week 2', amount: 52000, cases: 28 },
  { week: 'Week 3', amount: 48000, cases: 22 },
  { week: 'Week 4', amount: 61000, cases: 35 },
  { week: 'Week 5', amount: 58000, cases: 31 },
  { week: 'Week 6', amount: 67000, cases: 38 },
];

export default function Analytics() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState('6months');

  const handleRefresh = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const handleExport = () => {
    // Implement export functionality
    console.log('Exporting analytics data...');
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <div>
          <Typography variant="h4" component="h1" gutterBottom>
            Analytics
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Performance metrics and business intelligence
          </Typography>
        </div>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value="1month">1 Month</MenuItem>
              <MenuItem value="3months">3 Months</MenuItem>
              <MenuItem value="6months">6 Months</MenuItem>
              <MenuItem value="1year">1 Year</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Refresh'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleExport}
          >
            Export
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Collection Efficiency Trend */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Collection Efficiency Trend
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={collectionEfficiencyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value, name) => [
                  name === 'collected' || name === 'target' ? `$${value.toLocaleString()}` : `${value}%`,
                  name === 'collected' ? 'Collected' : name === 'target' ? 'Target' : 'Efficiency'
                ]} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="collected"
                  stackId="1"
                  stroke="#4caf50"
                  fill="#4caf50"
                  fillOpacity={0.6}
                  name="Collected"
                />
                <Area
                  type="monotone"
                  dataKey="target"
                  stackId="2"
                  stroke="#2196f3"
                  fill="#2196f3"
                  fillOpacity={0.3}
                  name="Target"
                />
                <Line
                  type="monotone"
                  dataKey="efficiency"
                  stroke="#ff9800"
                  strokeWidth={3}
                  name="Efficiency %"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Risk Distribution */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Customer Risk Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={riskDistributionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {riskDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Agent Performance */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Agent Performance Metrics
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={agentEfficiencyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="agent" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="tasks" fill="#2196f3" name="Total Tasks" />
                <Bar dataKey="success" fill="#4caf50" name="Successful" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Communication Channel Success */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Communication Channel Success Rates
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={communicationChannelData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="channel" />
                <YAxis />
                <Tooltip formatter={(value, name) => [
                  name === 'rate' ? `${value}%` : value,
                  name === 'sent' ? 'Sent' : name === 'successful' ? 'Successful' : 'Success Rate'
                ]} />
                <Legend />
                <Bar dataKey="sent" fill="#ff9800" name="Sent" />
                <Bar dataKey="successful" fill="#4caf50" name="Successful" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Recovery Trend */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Weekly Recovery Trend
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={recoveryTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip formatter={(value, name) => [
                  name === 'amount' ? `$${value.toLocaleString()}` : value,
                  name === 'amount' ? 'Amount Recovered' : 'Cases Resolved'
                ]} />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="amount"
                  stroke="#4caf50"
                  strokeWidth={3}
                  name="Amount Recovered"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="cases"
                  stroke="#2196f3"
                  strokeWidth={3}
                  name="Cases Resolved"
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Key Performance Indicators */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Key Performance Indicators
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      91.2%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Overall Collection Rate
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      $2.4M
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Recovered (6M)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">
                      28 days
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg. Resolution Time
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main">
                      96.5%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Agent Uptime
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}